import { inject, Injectable } from '@angular/core';
import { lastValueFrom, take } from 'rxjs';
import { AuthProfile } from '../interfaces/auth.interface';
import { AuthForm } from '../types/auth-form.type';
import { HttpClient } from '@angular/common/http';
import { injectMutation } from '@tanstack/angular-query-experimental';
import { Router } from '@angular/router';
import { environment } from '../../../../environments/environment';
import { IdentityNavigationService } from '../../identity/services/identity-navigation.service';
import { AuthSessionService } from './auth-session.service';
import { GaEventService } from '../../../core/services/ga.service';
import {
  PARENT_ANALYTICS_LAYOUT,
  ParentAnalyticsEvents,
} from '../../../core/analytics/parent-analytics.events';
import { SubdomainService } from '../../../core/services/subdomain.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  #http = inject(HttpClient);
  #router = inject(Router);
  #identityNav = inject(IdentityNavigationService);
  #session = inject(AuthSessionService);
  #analytics = inject(GaEventService);
  #subdomain = inject(SubdomainService);

  readonly isAuthenticated = this.#session.isAuthenticated;
  readonly accessToken = this.#session.accessToken;

  signin = injectMutation(() => ({
    mutationFn: async (data: { form: AuthForm; layout?: string }) => {
      const layout = data.layout || 'parent';
      const profile = await lastValueFrom(
        this.#http
          .post<AuthProfile>(`${environment.baseUrl}/auth/signin`, data.form)
          .pipe(take(1))
      );
      return { profile, layout };
    },
    onSuccess: async ({ profile, layout }) => {
      this.setAccessToken(profile.accessToken);
      await this.navigateAfterCredentialAuth(layout);
    },
  }));

  signup = injectMutation(() => ({
    mutationFn: async (data: {
      form: Record<string, string | null | undefined>;
      layout?: string;
    }) => {
      const layout = data.layout || 'parent';
      const body = {
        userName: String(data.form['userName'] ?? '').trim(),
        password: String(data.form['password'] ?? ''),
        email: data.form['email']?.trim() || undefined,
        firstName: data.form['firstName']?.trim() || undefined,
        lastName: data.form['lastName']?.trim() || undefined,
        country: data.form['country']?.trim() || undefined,
      };
      const profile = await lastValueFrom(
        this.#http
          .post<AuthProfile>(
            `${environment.baseUrl}/auth/signup-${layout}`,
            body,
          )
          .pipe(take(1)),
      );
      return { profile, layout };
    },
    onSuccess: async ({ profile, layout }) => {
      this.setAccessToken(profile.accessToken);
      await this.navigateAfterCredentialAuth(layout);
    },
  }));

  resetPasswordRequest = injectMutation(() => ({
    mutationFn: async (data: { userName: string }) =>
      lastValueFrom(
        this.#http
          .post(`${environment.baseUrl}/auth/reset-password-request`, data)
          .pipe(take(1))
      ),
  }));

  updatePassword = injectMutation(() => ({
    mutationFn: async (data: {
      userName: string;
      code: string;
      password: string;
    }) =>
      lastValueFrom(
        this.#http
          .post(`${environment.baseUrl}/auth/update-password`, data)
          .pipe(take(1))
      ),
  }));

  googleAuthMutation = injectMutation(() => ({
    mutationFn: (idToken: string) =>
      lastValueFrom(
        this.#http.post<AuthProfile>(`${environment.baseUrl}/auth/google`, {
          idToken,
        })
      ),
    onSuccess: async (data) => {
      this.setAccessToken(data.accessToken);
      this.#analytics.sendEvent(ParentAnalyticsEvents.SIGNIN, {
        method: 'google',
        user_role: 'parent',
        layout: PARENT_ANALYTICS_LAYOUT,
      });
      await this.#identityNav.navigateAfterAuth('parent');
    },
  }));

  clearSession(): void {
    this.#session.clearAccessToken();
  }

  logout() {
    if (this.#subdomain.isFamilySubdomain()) {
      this.#analytics.sendEvent(ParentAnalyticsEvents.SIGNOUT, {
        layout: PARENT_ANALYTICS_LAYOUT,
      });
    }
    this.clearSession();
    void this.#router.navigate(['/auth']);
  }

  private async navigateAfterCredentialAuth(layout: string): Promise<void> {
    const oauthReturn = sessionStorage.getItem('valvur_oauth_return');
    if (oauthReturn) {
      sessionStorage.removeItem('valvur_oauth_return');
      await this.#router.navigateByUrl(oauthReturn);
      return;
    }

    await this.#identityNav.navigateAfterAuth(layout);
  }

  private setAccessToken(token: string | null) {
    this.#session.setAccessToken(token);
  }
}
